import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Stack, Typography, Paper, Alert, CircularProgress, IconButton, Tooltip, Button, Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { BRAND } from "../styles/theme";
import { getBindBalance } from "../services/bind";
import { getSaldos, parseMonto, CMF_CUENTA_DEFAULT } from "../services/cmf";
import { getCollectors, fmtMoney, CUENTA_CMF_COMPARTIDA, type Collectors } from "../services/monitoreoVivo";
import { fmtArDateTime } from "../services/monitoring";
import { logout } from "../services/auth";
import { jwtVencido } from "../lib/jwt";
import {
  resumir, diferencia, universoBind, universoCmf, UMBRAL_DIFERENCIA_PCT,
  type ResumenCollectors, type NivelDiferencia,
} from "../lib/saldosDashboard";

/**
 * CP-468 — Dashboard v1: control de saldo en tiempo real por procesador.
 * Alcance (Pablo García, 12/10): solo control de saldos, sin tabs. Una tarjeta
 * por procesador: saldo del banco vs. "Neto" de sus collectors + diferencia.
 * La cuenta / universo de cada tarjeta está documentado en lib/saldosDashboard.ts.
 *
 * Refresco: todas las fuentes cada 2 minutos, para no cargar el backend ni los
 * bancos (collectors rearma ~1400 lecturas DynamoDB; Bind es un llamado en vivo
 * al banco; CMF tiene un rate limit de 10 req/min compartido con el job
 * debin-index). Con la pestaña oculta no se pollea. El refresco automático se puede pausar con un botón (la elección se
 * recuerda por navegador); pausado solo se consulta al entrar o con "Actualizar".
 * Si una fuente falla, solo su tarjeta muestra el error.
 */

const REFRESCO_MS = 120_000;

const AUTO_KEY = "webadmin2.dashboard.auto";

const BIND_CUENTA = "20-1-777983-1-5";
/** Nombre provisorio de "Neto" (pendiente de definir por Pablo). */
const NETO_LABEL = "A favor de collectors";
const NETO_HELP  = "\"Neto\" de Estado de collectors: disponible + pendiente × (1 − comisión). Es lo que se les debe a los collectors que cobran en esta cuenta.";

const pf = new Intl.NumberFormat("es-AR", { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 });
const ars = (n: number) => fmtMoney(n, "ARS");

interface Fuente<T> { data: T | null; error: string | null; at: string | null; loading: boolean }

/** Polling de una fuente: carga al montar, cada `ms` si `auto` y la pestaña está visible. */
function useFuente<T>(fn: () => Promise<T>, ms: number, auto: boolean): Fuente<T> & { reload: () => void } {
  const [s, setS] = useState<Fuente<T>>({ data: null, error: null, at: null, loading: false });
  const busy = useRef(false);
  const reload = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setS((p) => ({ ...p, loading: true }));
    try {
      const data = await fn();
      setS({ data, error: null, at: new Date().toISOString(), loading: false });
    } catch (e: any) {
      // Se conserva el último dato bueno: el error se muestra encima.
      setS((p) => ({ ...p, error: String(e?.message ?? e), loading: false }));
    } finally {
      busy.current = false;
    }
  }, [fn]);
  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => { if (document.visibilityState === "visible") void reload(); }, ms);
    return () => clearInterval(id);
  }, [auto, ms, reload]);
  return { ...s, reload };
}

/**
 * CP-468: la sesión de CoreDB (`webadmin2.corejwt`) dura 4 h y la de webadmin2 5 h,
 * así que el saldo de Bind empieza a dar 401 antes que el resto. Si el token ya
 * venció se avisa con un botón para volver a iniciar sesión (en sandbox el 401
 * llega con el token vigente y queda como error común).
 */
const SESION_VENCIDA = "SESION_COREDB_VENCIDA";
const coreSesionVencida = () => {
  try { return jwtVencido(localStorage.getItem("webadmin2.corejwt")); } catch { return false; }
};
const fetchBind = async () => {
  if (coreSesionVencida()) throw new Error(SESION_VENCIDA);
  try { return await getBindBalance({ redirectOn401: false }); }
  catch (e) { if (coreSesionVencida()) throw new Error(SESION_VENCIDA); throw e; }
};
const reLogin = () => { logout(); window.location.replace("/login"); };
const fetchCmf = async () => {
  const cuentas = await getSaldos();
  const c = cuentas.find((x) => x.numero_cuenta === CMF_CUENTA_DEFAULT);
  if (!c) throw new Error(`CMF no devolvió la cuenta ${CMF_CUENTA_DEFAULT}.`);
  return { disponible: parseMonto(c.saldos?.disponible), real: parseMonto(c.saldos?.real) };
};

export default function MonitoreoDashboard() {
  // Preferencia por navegador; si el storage no está disponible arranca activo.
  const [auto, setAuto] = useState<boolean>(() => {
    try { return localStorage.getItem(AUTO_KEY) !== "off"; } catch { return true; }
  });
  const toggleAuto = () => setAuto((a) => {
    try { localStorage.setItem(AUTO_KEY, a ? "off" : "on"); } catch { /* sin storage: no se recuerda */ }
    return !a;
  });
  const col  = useFuente<Collectors>(getCollectors, REFRESCO_MS, auto);
  const bind = useFuente(fetchBind, REFRESCO_MS, auto);
  const cmf  = useFuente(fetchCmf, REFRESCO_MS, auto);

  const resBind = useMemo(() => (col.data ? resumir(col.data.rows, universoBind) : null), [col.data]);
  const resCmf  = useMemo(() => (col.data ? resumir(col.data.rows, universoCmf(CUENTA_CMF_COMPARTIDA)) : null), [col.data]);
  const loading = col.loading || bind.loading || cmf.loading;
  const reloadAll = () => { col.reload(); bind.reload(); cmf.reload(); };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5">Dashboard · control de saldos</Typography>
          <Typography variant="body2" color="text.secondary">
            Saldo de cada cuenta del procesador contra lo que se les debe a los collectors que cobran en ella.
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={auto
            ? "Refresco automático activo: se actualiza cada 2 minutos. Pausalo para no consultar mientras tenés la página abierta."
            : "Refresco automático pausado: los datos solo se actualizan con el botón de la derecha."}>
            <Button variant={auto ? "outlined" : "contained"} color={auto ? "primary" : "warning"}
              startIcon={auto ? <PauseIcon /> : <PlayArrowIcon />} onClick={toggleAuto}>
              {auto ? "Pausar refresco" : "Reanudar refresco"}
            </Button>
          </Tooltip>
          <Tooltip title="Actualizar todo"><span>
            <IconButton onClick={reloadAll} disabled={loading}>{loading ? <CircularProgress size={20} /> : <RefreshIcon />}</IconButton>
          </span></Tooltip>
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={3} alignItems="stretch">
        <TarjetaProcesador
          titulo="Bind · cuenta por defecto" cuenta={`BindPSP ${BIND_CUENTA}`}
          universo="Collectors AR#BIND sin cuenta propia"
          saldo={bind.data ?? null} saldoErr={bind.error} saldoAt={bind.at} saldoCargando={bind.loading && bind.at == null}
          res={resBind} colErr={col.error} colAt={col.at}
          nota="Los collectors con cuenta BindPSP propia no tienen saldo consultable y quedan afuera."
        />
        <TarjetaProcesador
          titulo="CMF · cuenta recaudadora" cuenta={`Cuenta ${CMF_CUENTA_DEFAULT}`}
          universo="Collectors AR#CMFP de la cuenta compartida"
          saldo={cmf.data?.disponible ?? null} saldoErr={cmf.error} saldoAt={cmf.at} saldoCargando={cmf.loading && cmf.at == null}
          saldoSub={cmf.data ? `Real ${ars(cmf.data.real)}` : undefined}
          res={resCmf} colErr={col.error} colAt={col.at}
          nota="Saldo = disponible de CMF (el real va debajo)."
        />
      </Stack>

      <Alert severity="info" sx={{ mt: 3 }}>
        La diferencia incluye las comisiones ya cobradas y todavía no retiradas (la v2 las va a restar), los pendientes de días
        anteriores (el pendiente solo mira el día de negocio actual) y las devoluciones de rechazos en curso. Una diferencia
        <b> negativa</b> significa que en el banco hay menos de lo que se les debe a los collectors.
        {UMBRAL_DIFERENCIA_PCT == null && " Umbral de alerta para diferencias positivas: pendiente de definir."}
      </Alert>
    </Box>
  );
}

const NIVEL_COLOR: Record<NivelDiferencia, string> = { ok: "#166534", warn: "#9A6700", bad: "#B91C1C" };

function TarjetaProcesador(p: {
  titulo: string; cuenta: string; universo: string; nota: string;
  saldo: number | null; saldoErr: string | null; saldoAt: string | null; saldoCargando: boolean; saldoSub?: string;
  res: ResumenCollectors | null; colErr: string | null; colAt: string | null;
}) {
  const dif = p.saldo != null && p.res ? diferencia(p.saldo, p.res.neto) : null;
  return (
    <Paper variant="outlined" sx={{ flex: 1, p: 2.5, borderRadius: 2, minWidth: 0 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>{p.titulo}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{p.cuenta} · {p.universo}</Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} useFlexGap sx={{ mb: 2 }}>
        <Cifra label="Saldo en el banco" cargando={p.saldoCargando}
          valor={p.saldo != null ? ars(p.saldo) : "—"} sub={p.saldoSub} />
        <Cifra label={NETO_LABEL} help={NETO_HELP} cargando={!p.res && !p.colErr}
          valor={p.res ? ars(p.res.neto) : "—"} />
        <Cifra label="Diferencia" cargando={false}
          valor={dif ? ars(dif.monto) : "—"} color={dif ? NIVEL_COLOR[dif.nivel] : undefined}
          sub={dif?.pct != null ? `${pf.format(dif.pct)} del saldo` : undefined} />
      </Stack>

      {p.saldoErr === SESION_VENCIDA ? (
        <Alert severity="warning" sx={{ mb: 1 }}
          action={<Button color="inherit" size="small" onClick={reLogin}>Iniciar sesión</Button>}>
          Tu sesión de CoreDB venció (dura 4 h): el saldo del banco no se está actualizando. Volvé a iniciar sesión.
        </Alert>
      ) : p.saldoErr && <Alert severity="error" sx={{ mb: 1 }}>Saldo del banco: {p.saldoErr}</Alert>}
      {p.colErr && <Alert severity="error" sx={{ mb: 1 }}>Collectors: {p.colErr}</Alert>}

      {p.res && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
            Desglose de collectors
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", columnGap: 2, rowGap: 0.5, mt: 1, fontVariantNumeric: "tabular-nums" }}>
            <Linea label="Disponible" valor={ars(p.res.disponible)} />
            <Linea label="Pendiente (día de negocio)" valor={ars(p.res.pendiente)} />
            <Linea label="− Comisión estimada sobre pendiente" valor={ars(p.res.comisionPendiente)} />
            <Linea label={`= ${NETO_LABEL}`} valor={ars(p.res.neto)} fuerte />
            <Linea label="Collectors con saldo" valor={`${p.res.conSaldo} de ${p.res.collectors}`} />
          </Box>
        </>
      )}

      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
        Banco: {p.saldoAt ? fmtArDateTime(p.saldoAt) : "—"} · Collectors: {p.colAt ? fmtArDateTime(p.colAt) : "—"}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div">{p.nota}</Typography>
    </Paper>
  );
}

function Cifra({ label, valor, sub, color, help, cargando }: {
  label: string; valor: string; sub?: string; color?: string; help?: string; cargando: boolean;
}) {
  const head = (
    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "text.secondary" }}>{label}</Typography>
  );
  return (
    <Box sx={{ bgcolor: BRAND.primaryLight, borderRadius: 2, px: 2, py: 1.5, flex: 1, minWidth: 0 }}>
      {help ? <Tooltip title={help}><Box component="span" sx={{ cursor: "help" }}>{head}</Box></Tooltip> : head}
      {cargando
        ? <CircularProgress size={22} sx={{ mt: 1 }} />
        : <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, mt: 0.5, color: color ?? BRAND.primary, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere" }}>{valor}</Typography>}
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Box>
  );
}

function Linea({ label, valor, fuerte }: { label: string; valor: string; fuerte?: boolean }) {
  return (
    <>
      <Typography variant="body2" sx={{ fontWeight: fuerte ? 700 : 400 }}>{label}</Typography>
      <Typography variant="body2" sx={{ textAlign: "right", fontWeight: fuerte ? 700 : 400 }}>{valor}</Typography>
    </>
  );
}
